import { contattoInputSchema, RUOLI_CONTATTO, type Contatto, type ContattoInput } from '@aulaviva/shared';
import { z } from 'zod';
import { Alert, Badge, Button, Checkbox, DataTable, Dialog, EmptyState, NativeSelect, TextArea, TextField, useToast, type ColumnDef } from '@aulaviva/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck, CircleOff, Plus, UserRoundCog } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useContatti, useContattiAvvisi, useDisattivaContatto, useSalvaContatto } from '@/api/amministrazione';
import { formattaDataBreve, inputLocaleAIso, isoAInputLocale } from '@/lib/date';
import { admin, amministrazione, comuni } from '@/testi';

const t = amministrazione.contatti;

function daInput(iso: string | null | undefined): string {
  return iso ? isoAInputLocale(iso).slice(0, 10) : '';
}

export function SchedaContatti() {
  const toast = useToast();
  const contatti = useContatti();
  const avvisi = useContattiAvvisi();
  const salva = useSalvaContatto();
  const disattiva = useDisattivaContatto();
  const [dialogo, setDialogo] = useState<'nuovo' | Contatto | null>(null);
  const [daDisattivare, setDaDisattivare] = useState<Contatto | null>(null);

  const colonne = useMemo<ColumnDef<Contatto, unknown>[]>(
    () => [
      { header: t.colonne.ruolo, cell: ({ row }) => t.ruoli[row.original.ruolo] },
      { header: t.colonne.nome, accessorKey: 'nome' },
      { header: t.colonne.email, cell: ({ row }) => row.original.email || '—' },
      { header: t.colonne.telefono, cell: ({ row }) => <span className="t-code text-[13px]">{row.original.telefono || '—'}</span> },
      {
        header: t.colonne.riceve,
        cell: ({ row }) => {
          const c = row.original;
          const voci = [c.riceveReport && t.badge.report, c.riceveAvvisi && t.badge.avvisi, c.riceveChiamate && t.badge.chiamate].filter(Boolean);
          return voci.length ? voci.join(' · ') : t.badge.nessuno;
        },
      },
      {
        header: t.colonne.attivo,
        cell: ({ row }) =>
          row.original.attivo ? (
            <Badge variant="success" icon={CircleCheck}>
              {t.stati.attivo}
            </Badge>
          ) : (
            <Badge variant="neutral" icon={CircleOff}>
              {t.stati.disattivato}
            </Badge>
          ),
      },
      {
        id: 'azioni',
        header: '',
        cell: ({ row }) => (
          <span className="inline-flex gap-1">
            <Button variant="discreet" onClick={() => setDialogo(row.original)}>
              {admin.comuni.modifica}
            </Button>
            {row.original.attivo && (
              <Button variant="discreet" onClick={() => setDaDisattivare(row.original)}>
                {t.disattiva}
              </Button>
            )}
          </span>
        ),
      },
    ],
    [],
  );

  async function confermaDisattiva() {
    if (!daDisattivare) return;
    try {
      await disattiva.mutateAsync(daDisattivare.id);
      setDaDisattivare(null);
      toast.mostra({ variant: 'success', title: t.disattivato });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <p className="t-body text-ink-muted max-w-[640px]">{t.intro}</p>
        <Button variant="primary" icon={Plus} onClick={() => setDialogo('nuovo')}>
          {t.nuovo}
        </Button>
      </div>
      {avvisi.data?.report && <Alert variant="warning" title={t.avvisoReport} />}
      {avvisi.data?.chiamate && <Alert variant="warning" title={t.avvisoChiamate} />}
      {contatti.isError && <Alert variant="error" title={contatti.error.message} />}
      <DataTable
        caption={amministrazione.schede.contatti}
        columns={colonne}
        data={contatti.data ?? []}
        getRowId={(c) => c.id}
        loading={contatti.isPending}
        testi={admin.comuni}
        empty={
          <EmptyState icon={UserRoundCog} title={t.nessunoTitolo}>
            {t.nessunoTesto}
          </EmptyState>
        }
      />
      {dialogo && (
        <DialogoContatto
          contatto={dialogo === 'nuovo' ? null : dialogo}
          onChiudi={() => setDialogo(null)}
          onSalva={async (dati) => {
            await salva.mutateAsync({ id: dialogo === 'nuovo' ? undefined : dialogo.id, dati });
            setDialogo(null);
            toast.mostra({ variant: 'success', title: t.salvato });
          }}
          loading={salva.isPending}
        />
      )}
      <Dialog
        open={daDisattivare !== null}
        onOpenChange={(o) => !o && setDaDisattivare(null)}
        title={daDisattivare ? t.disattivaTitolo(daDisattivare.nome) : ''}
        description={t.disattivaTesto}
        actions={
          <>
            <Button onClick={() => setDaDisattivare(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={confermaDisattiva} loading={disattiva.isPending} loadingText={admin.comuni.inCorso}>
              {t.disattivaConferma}
            </Button>
          </>
        }
      />
    </>
  );
}

/** Modulo del contatto: le date come stringhe `YYYY-MM-DD` dell'input nativo. */
const formSchema = z.object({
  ruolo: z.enum(RUOLI_CONTATTO),
  nome: contattoInputSchema.shape.nome,
  email: contattoInputSchema.shape.email,
  telefono: contattoInputSchema.shape.telefono,
  riceveReport: z.boolean(),
  riceveAvvisi: z.boolean(),
  riceveChiamate: z.boolean(),
  note: contattoInputSchema.shape.note,
  attivoDalData: z.string().optional(),
  attivoAlData: z.string().optional(),
});
type FormContatto = z.infer<typeof formSchema>;

function DialogoContatto({ contatto, onChiudi, onSalva, loading }: { contatto: Contatto | null; onChiudi: () => void; onSalva: (dati: ContattoInput) => Promise<void>; loading: boolean }) {
  const [errore, setErrore] = useState<string | null>(null);
  const { register, control, handleSubmit, formState } = useForm<FormContatto>({
    resolver: zodResolver(formSchema),
    defaultValues: contatto
      ? { ...contatto, email: contatto.email ?? '', telefono: contatto.telefono ?? '', note: contatto.note ?? '', attivoDalData: daInput(contatto.attivoDal), attivoAlData: daInput(contatto.attivoAl) }
      : { ruolo: 'organizzatore', nome: '', email: '', telefono: '', riceveReport: false, riceveAvvisi: false, riceveChiamate: false, note: '', attivoDalData: daInput(new Date().toISOString()), attivoAlData: '' },
  });

  async function invia(f: FormContatto) {
    setErrore(null);
    const { attivoDalData, attivoAlData, ...resto } = f;
    const dati: ContattoInput = {
      ...resto,
      attivoDal: attivoDalData ? inputLocaleAIso(`${attivoDalData}T00:00`) : undefined,
      attivoAl: attivoAlData ? inputLocaleAIso(`${attivoAlData}T23:59`) : null,
    };
    try {
      await onSalva(dati);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onChiudi()}
      title={contatto ? t.modificaTitolo(contatto.nome) : t.nuovoTitolo}
      actions={
        <>
          <Button onClick={onChiudi}>{admin.comuni.annulla}</Button>
          <Button variant="primary" type="submit" form="form-contatto" loading={loading} loadingText={admin.comuni.inCorso}>
            {t.salva}
          </Button>
        </>
      }
    >
      <form id="form-contatto" onSubmit={handleSubmit(invia)} noValidate className="flex flex-col gap-4">
        {errore && <Alert variant="error" title={errore} />}
        <NativeSelect label={t.campi.ruolo} options={RUOLI_CONTATTO.map((r) => ({ value: r, label: t.ruoli[r] }))} error={formState.errors.ruolo?.message} {...register('ruolo')} />
        <TextField label={t.campi.nome} autoComplete="name" error={formState.errors.nome?.message} {...register('nome')} />
        <TextField label={t.campi.email} type="email" autoComplete="email" error={formState.errors.email?.message} {...register('email')} />
        <TextField label={t.campi.telefono} type="tel" hint={t.campi.telefonoAiuto} mono error={formState.errors.telefono?.message} {...register('telefono')} />
        <Controller control={control} name="riceveReport" render={({ field }) => <Checkbox checked={!!field.value} onCheckedChange={field.onChange} label={t.campi.riceveReport} />} />
        <Controller control={control} name="riceveAvvisi" render={({ field }) => <Checkbox checked={!!field.value} onCheckedChange={field.onChange} label={t.campi.riceveAvvisi} />} />
        <Controller control={control} name="riceveChiamate" render={({ field }) => <Checkbox checked={!!field.value} onCheckedChange={field.onChange} label={t.campi.riceveChiamate} />} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label={t.campi.attivoDal} type="date" {...register('attivoDalData')} />
          <TextField label={t.campi.attivoAl} type="date" hint={t.campi.attivoAlAiuto} {...register('attivoAlData')} />
        </div>
        <TextArea label={t.campi.note} rows={2} error={formState.errors.note?.message} {...register('note')} />
        {contatto && !contatto.attivo && <p className="t-body-small text-ink-muted">{formattaDataBreve(contatto.attivoDal)}</p>}
      </form>
    </Dialog>
  );
}
